import React, { useEffect } from 'react'
import { Routes, Route, Link, Navigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useInstitution } from '../../contexts/InstitutionContext'
import './InstitutionApp.css'

const InstitutionApp = () => {
  const { currentUser, logout } = useAuth()
  const { institution, theme, applyTheme, institutionName } = useInstitution()

  // Apply institution theming
  useEffect(() => {
    applyTheme()
  }, [theme, applyTheme])

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }

  return (
    <div className="institution-platform">
      <header className="institution-header">
        <div className="institution-nav">
          <div className="institution-brand">
            {institution?.logo_url && (
              <img 
                src={institution.logo_url} 
                alt={institutionName} 
                className="institution-logo"
              />
            )}
            <h1>{institutionName} Portal</h1>
          </div>
          <nav className="institution-navigation">
            <Link to="/institution/dashboard" className="nav-link">Dashboard</Link>
            <Link to="/institution/print-orders" className="nav-link">Print Orders</Link>
            <Link to="/institution/gcode-library" className="nav-link">G-code Files</Link>
            <Link to="/institution/billing" className="nav-link">Billing</Link>
            <Link to="/institution/users" className="nav-link">Users</Link>
            <Link to="/institution/settings" className="nav-link">Settings</Link>
          </nav>
        </div>
        <div className="institution-user-info">
          <span className="institution-badge">INSTITUTION ADMIN</span>
          <span>{currentUser?.displayName || currentUser?.email}</span>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      <main className="institution-main">
        <Routes>
          <Route path="/dashboard" element={<InstitutionDashboard />} />
          <Route path="/print-orders" element={<PrintOrdersView />} />
          <Route path="/gcode-library" element={<GCodeLibraryView />} />
          <Route path="/billing" element={<BillingView />} />
          <Route path="/users" element={<UsersView />} />
          <Route path="/settings" element={<SettingsView />} />
          <Route path="/" element={<Navigate to="/institution/dashboard" />} />
        </Routes>
      </main>
    </div>
  )
}

const InstitutionDashboard = () => (
  <div className="institution-dashboard">
    <h2>Institution Dashboard</h2>
    <div className="dashboard-stats">
      <div className="stat-card">
        <h3>Active Users</h3>
        <div className="stat-value">0</div>
        <div className="stat-label">Students & Staff</div>
      </div>
      <div className="stat-card">
        <h3>Print Orders</h3>
        <div className="stat-value">0</div>
        <div className="stat-label">This month</div>
      </div>
      <div className="stat-card">
        <h3>Monthly Revenue</h3>
        <div className="stat-value">$0</div>
        <div className="stat-label">From print services</div>
      </div>
      <div className="stat-card">
        <h3>G-code Files</h3>
        <div className="stat-value">0</div>
        <div className="stat-label">Ready for printing</div>
      </div>
    </div>
    
    <div className="dashboard-grid">
      <div className="dashboard-card">
        <h3>Print Order Management</h3>
        <p>Manage client print requests and billing</p>
        <Link to="/institution/print-orders" className="card-action">Manage Orders</Link>
      </div>
      <div className="dashboard-card">
        <h3>G-code Library</h3>
        <p>Download and manage processed G-code files</p>
        <Link to="/institution/gcode-library" className="card-action">Browse Files</Link>
      </div>
      <div className="dashboard-card">
        <h3>User Management</h3>
        <p>Add and manage students and staff accounts</p>
        <Link to="/institution/users" className="card-action">Manage Users</Link>
      </div>
      <div className="dashboard-card">
        <h3>Billing & Revenue</h3>
        <p>Track revenue and manage client billing</p>
        <Link to="/institution/billing" className="card-action">View Billing</Link>
      </div>
    </div>
  </div>
)

const PrintOrdersView = () => (
  <div className="print-orders-view">
    <div className="view-header">
      <h2>Print Order Management</h2>
      <button className="primary-btn">New Print Order</button>
    </div>
    <div className="coming-soon-large">
      <h3>Print Order Management Features:</h3>
      <ul>
        <li>Create print orders for clients</li>
        <li>Set pricing and material costs</li>
        <li>Track order status and completion</li>
        <li>Generate invoices automatically</li>
        <li>Download G-code files for printing</li>
        <li>Client communication and updates</li>
      </ul>
      <p><strong>Status:</strong> Ready for implementation</p>
    </div>
  </div>
)

const GCodeLibraryView = () => (
  <div className="gcode-library-view">
    <div className="view-header">
      <h2>G-code File Library</h2>
      <div className="view-actions">
        <button className="secondary-btn">Filter</button>
        <button className="secondary-btn">Sort</button>
      </div>
    </div>
    <div className="coming-soon-large">
      <h3>G-code Library Features:</h3>
      <ul>
        <li>Browse all processed G-code files</li>
        <li>Download files individually or in batches</li>
        <li>Preview 3D models before printing</li>
        <li>Search and filter by project or user</li>
        <li>View file metadata and print settings</li>
        <li>Archive and organize files</li>
      </ul>
      <p><strong>Status:</strong> Ready for implementation</p>
    </div>
  </div>
)

const BillingView = () => (
  <div className="billing-view">
    <div className="view-header">
      <h2>Billing & Revenue</h2>
      <button className="primary-btn">Generate Invoice</button>
    </div>
    <div className="coming-soon-large">
      <h3>Billing Management Features:</h3>
      <ul>
        <li>Track revenue from print services</li>
        <li>Generate client invoices</li>
        <li>Set pricing tiers and materials costs</li>
        <li>View payment history and outstanding balances</li>
        <li>Export financial reports</li>
        <li>Integrate with payment processors</li>
      </ul>
      <p><strong>Status:</strong> Ready for implementation</p>
    </div>
  </div>
)

const UsersView = () => (
  <div className="users-view">
    <div className="view-header">
      <h2>User Management</h2>
      <button className="primary-btn">Add User</button>
    </div>
    <div className="coming-soon-large">
      <h3>User Management Features:</h3>
      <ul>
        <li>Add students and staff to your institution</li>
        <li>Assign roles and permissions</li>
        <li>Monitor user activity and usage</li>
        <li>Set quotas and limits per user</li>
        <li>Bulk user imports via CSV</li>
        <li>User account activation and deactivation</li>
      </ul>
      <p><strong>Status:</strong> Ready for implementation</p>
    </div>
  </div>
)

const SettingsView = () => (
  <div className="settings-view">
    <h2>Institution Settings</h2>
    <div className="coming-soon-large">
      <h3>Institution Settings Features:</h3>
      <ul>
        <li>Configure institution branding and logo</li>
        <li>Set default printer and material settings</li>
        <li>Manage license tier and features</li>
        <li>Configure billing and payment settings</li>
        <li>Set user quotas and limits</li>
        <li>Integration settings and API keys</li>
      </ul>
      <p><strong>Status:</strong> Ready for implementation</p>
    </div>
  </div>
)

export default InstitutionApp 